import { Link } from 'react-router-dom';
import { ReceiptIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { useSendToSpace } from '@/hooks/useSendToSpace';
import { ruleLabel } from '@/lib/spaces/rules';
import { categoryChipStyleFromColor, resolveCategoryColor } from '@/lib/transactions/categories';
import type { SpacePendingSend, SpaceRulesOverview } from '@/types/spaces';

export function SpaceRulesPending({ entry, refreshing }: { entry: SpaceRulesOverview; refreshing: boolean }) {
  const renderPending = (pending: SpacePendingSend) => <PendingRow key={pending.transactionId} pending={pending} entry={entry} />;
  if (refreshing) return <div role="status" aria-busy="true" className="space-y-2">
    <p className="text-xs text-text-secondary">Atualizando contagens e próximos envios…</p>
    <Skeleton className="h-16 rounded-xl" />
  </div>;
  return <div className="space-y-3">
    <dl className="grid grid-cols-2 gap-2 text-center">
      <div className="rounded-xl bg-surface-sunken p-3">
        <dt className="text-xs text-text-secondary">Entraram por regra</dt>
        <dd className="mt-1 text-sm"><span className="font-mono tabular-nums">{entry.sentByRulesCount.toLocaleString('pt-BR')}</span> <span className="text-xs text-text-secondary">de {entry.sentCount.toLocaleString('pt-BR')} suas</span></dd>
      </div>
      <div className="rounded-xl bg-surface-sunken p-3">
        <dt className="text-xs text-text-secondary">Vão entrar na próxima sync</dt>
        <dd className="mt-1 font-mono text-sm tabular-nums">{entry.pendingCount.toLocaleString('pt-BR')}</dd>
      </div>
    </dl>
    <p className="text-xs text-text-secondary">Previsão com os lançamentos disponíveis agora. Pode mudar até a próxima sincronização.</p>
    <ul aria-label="Lançamentos aguardando envio" className="divide-y divide-border-subtle">{entry.pending.map(renderPending)}</ul>
    <MorePending entry={entry} />
  </div>;
}

function PendingRow({ pending, entry }: { pending: SpacePendingSend; entry: SpaceRulesOverview }) {
  const send = useSendToSpace();
  const format = useFormatCurrency();
  const rule = entry.rules.find((item) => item.rule.id === pending.ruleId);
  const category = pending.categoryId ? categoryChipStyleFromColor(resolveCategoryColor(pending.categoryId)) : undefined;
  const date = new Date(pending.occurredAt).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  const handleSend = () => { void send?.openById(pending.transactionId, entry.space.id); };
  return <li className="flex flex-wrap items-center gap-2 py-3">
    <span className="grid size-8 shrink-0 place-items-center rounded-xl bg-surface-sunken text-text-secondary" style={category && { backgroundColor: category.backgroundColor, color: category.color }}>
      <ReceiptIcon className="size-4" aria-hidden />
    </span>
    <div className="min-w-0 flex-1 basis-1/2">
      <p className="break-words text-sm font-medium">{pending.description}</p>
      <p className="mt-1 break-words text-2xs text-text-secondary">{date} · {rule ? ruleLabel(rule) : 'Regra indisponível'}</p>
    </div>
    <div className="ml-auto flex items-center gap-1">
      <span className="font-mono text-xs tabular-nums">{format(pending.amountCents / 100)}</span>
      <Button variant="link" size="sm" onClick={handleSend} disabled={!send || send.resolving || Boolean(entry.space.archivedAt) || entry.space.myRole !== 'editor'}
        aria-label={`Enviar ${pending.description} agora para ${entry.space.name}`}>agora</Button>
    </div>
  </li>;
}

function MorePending({ entry }: { entry: SpaceRulesOverview }) {
  if (!entry.hasMorePending) return null;
  return <p className="text-xs text-text-secondary">Mostrando {entry.pending.length} de {entry.pendingCount.toLocaleString('pt-BR')} lançamentos. <Link to="/transactions" className="cursor-pointer font-semibold text-primary underline-offset-4 hover:underline">Ver transações</Link></p>;
}
